import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { useIntl } from 'react-intl';
import { DatasetMetadata } from '../adapters/types';
import ImageChoiceMenu from './ImageChoiceMenu';

interface RowImageMenuProps {
    /** The image the row draws now. */
    readonly current: DatasetMetadata;
    /** Every series the grid can bind, in the order the host offers them. */
    readonly available: ReadonlyArray<DatasetMetadata>;
    readonly onBind: (seriesInstanceUid: SeriesInstanceUid) => void;
}

/**
 * The image a row draws, and the list that changes it. Binding another
 * series keeps the row's panes and planes.
 */
export default function RowImageMenu({
    current,
    available,
    onBind,
}: RowImageMenuProps) {
    const intl = useIntl();
    // With nothing else loaded, a menu of one row that changes nothing reads
    // as a control that is broken.
    const hasAlternatives = available.some(
        (dataset) => dataset.seriesInstanceUid !== current.seriesInstanceUid
    );
    const disabledReason = hasAlternatives
        ? undefined
        : intl.formatMessage({ id: 'rowImage.noOtherImage' });

    return (
        <ImageChoiceMenu
            current={current}
            images={available}
            onChoose={onBind}
            ariaLabel={intl.formatMessage({ id: 'rowImage.label' })}
            testId="row-image-trigger"
            disabledReason={disabledReason}
            section={intl.formatMessage({ id: 'primaryImage' })}
        />
    );
}
